import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  IPractice,
  IPracticeStatus,
  IAiDialogHistoryItem,
} from "../types/generalTypes";
import { ActivePracticeContext } from "./contextExporter";

import { useAuth } from "../hooks/useAuth";
import { prepareUiHistory, removeTags, myAxios, transformSandpackAnswer } from "../utils/common";
import PracticeItemMain from "../modulez/practiceItem/PracticeItemMain";
import { defaultSpOptions } from "../modulez/practiceItem/adapters";
import { useGamification } from "../hooks/useGamification";
import CelebrationOverlay from "../components/CelebrationOverlay";

interface CelebrationState {
  isVisible: boolean;
  xpEarned: number;
  currentStreak: number;
  streakIncremented: boolean;
  score: number;
  freezeUsed: boolean;
}

const CELEBRATION_INITIAL: CelebrationState = {
  isVisible: false,
  xpEarned: 0,
  currentStreak: 0,
  streakIncremented: false,
  score: 0,
  freezeUsed: false,
};

export function ActivePracticeContextProvider({
  practice,
  isLive,
  unitId,
}: {
  practice: IPractice;
  isLive: boolean;
  unitId?: number;
}) {
  const initialActionStatus: IPracticeStatus = {
    ai: "IDLE",
    canGoNext: !isLive,
    historyIndex: -1,
  };
  const { user, isInstructor } = useAuth();
  const { updateCache } = useGamification();
  const queryClient = useQueryClient();
  const [aiDialogHistory, setAiDialogHistory] = useState<
    Record<string, IAiDialogHistoryItem[]>
  >({});

  const [wrongAnswerCount, setWrongAnswerCount] = useState(0);
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(isInstructor);
  const [actionStatus, setActionStatus] =
    useState<IPracticeStatus>(initialActionStatus);
  const [celebration, setCelebration] = useState<CelebrationState>(CELEBRATION_INITIAL);

  const dismissCelebration = useCallback(
    () => setCelebration(CELEBRATION_INITIAL),
    []
  );

  const sendAnswer = async (formData: { answer: string }) => {
    // Cevap görüldükten sonra gönderim yok (soru puansız geçilir)
    if (isAnswerRevealed && !isInstructor) return;

    const newActionStatus: IPracticeStatus = {
      ...actionStatus,
      ai: "LOADING",
      canGoNext: false,
    };
    setActionStatus(newActionStatus);

    const common = {
      userId: user?.id,
      username: user?.shortName,
      practiceId: practice.id,
      language:
        "lang" in practice.description && practice.description.lang
          ? practice.description.lang
          : "javascript",
      msgHistory: aiDialogHistory[practice.id],
      isLive,
      unitId,
    };

    let extra: Record<string, unknown> = {};

    switch (practice.type) {
      case "TEST":
        extra = {
          practiceType: "TEST",
          answer: formData.answer,
          question: removeTags(practice.description.title),
          options: removeTags(practice.description.options!.join("|")),
          correctAnswer: practice.description.answer,
        };
        break;
      case "CODE":
        extra = {
          practiceType: "CODE",
          answer: formData.answer,
          question: removeTags(practice.description.title),
          expectedCode: practice.description.answer,
          ...(practice.tags === "preSandpack"
            ? { spOptions: defaultSpOptions }
            : null),
        };
        break;
      case "PYTHON":
        extra = {
          practiceType: "PYTHON",
          answer: formData.answer,
          question: removeTags(practice.description.title),
          expectedCode: practice.description.answer,
          language: "python",
        };
        break;
      case "REACT":
        extra = {
          practiceType: "REACT",
          answer: formData.answer,
          question: removeTags(practice.description.title),
          expectedCode: transformSandpackAnswer(
            practice.description.spOptions!.wantedFiles!,
            practice.description!.answerFiles!
          ).answer,
        };
        break;
      default:
        break;
    }

    try {
      const response = await myAxios.post("/practice/evaluate", {
        ...common,
        ...extra,
      });

      const { aiResponse, score, history, xpEarned, totalXp, currentStreak, streakIncremented, freezeUsed, freezeCount, level, levelProgress } = response.data;

      // Dialog history güncelle
      const newHistory: IAiDialogHistoryItem[] = history || [];
      setAiDialogHistory((prev) => ({ ...prev, [practice.id!]: newHistory }));

      const isCorrectEnough = score > 0.79;

      if (practice.type === "TEST") {
        const isTestCorrect = formData.answer === practice.description.answer;
        setActionStatus({
          ai: "IDLE",
          canGoNext: isTestCorrect,
          historyIndex: 0,
        });
        if (!isTestCorrect) {
          setWrongAnswerCount((c) => c + 1);
        }
      } else {
        setActionStatus({
          ai: "IDLE",
          canGoNext: isCorrectEnough,
          historyIndex: 0,
        });
        if (!isCorrectEnough) {
          setWrongAnswerCount((c) => c + 1);
        }
      }

      // Invalidate practice history so checkmarks update
      queryClient.invalidateQueries({ queryKey: ["partPracticesHistory"] });

      // Gamification cache update (optimistic)
      if (xpEarned !== undefined) {
        updateCache({
          totalXp,
          currentStreak,
          freezeCount,
          // Pratik yapıldı → bekleyen freeze kullanımı tüketildi,
          // aksi halde stale değer freezeCount ile çelişir (ör. "0 Freeze + 1 kullanımda").
          freezeInUse: 0,
          level,
          levelProgress,
        });
        // Calendar'ın frozen günleri backend'den tekrar çekmesi için invalidate et
        queryClient.invalidateQueries({ queryKey: ["streakCalendar"] });

        // Celebration trigger
        setCelebration({
          isVisible: true,
          xpEarned,
          currentStreak,
          streakIncremented: streakIncremented ?? false,
          score,
          freezeUsed: freezeUsed ?? false,
        });
      }
    } catch (err) {
      console.error("Practice evaluate error:", err);
      setActionStatus({
        ai: "IDLE",
        canGoNext: false,
        historyIndex: -1,
      });
    }
  };

  useEffect(() => {
    setActionStatus(initialActionStatus);
    setWrongAnswerCount(0);
    setIsAnswerRevealed(false);
    setCelebration(CELEBRATION_INITIAL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice]);

  const initialHistory = aiDialogHistory[practice.id]
    ? prepareUiHistory(aiDialogHistory[practice.id])
    : [];

  return (
    <ActivePracticeContext.Provider
      value={{
        aiDialogHistory,
        setAiDialogHistory,
        actionStatus,
        setActionStatus,
        sendAnswer,
        practice,
        wrongAnswerCount,
        setWrongAnswerCount,
        isAnswerRevealed,
        setIsAnswerRevealed,
        isInstructor
      }}
    >
      <PracticeItemMain
        key={practice.id}
        initialHistory={initialHistory}
        isLive={isLive}
      />
      <CelebrationOverlay
        isVisible={celebration.isVisible}
        xpEarned={celebration.xpEarned}
        currentStreak={celebration.currentStreak}
        streakIncremented={celebration.streakIncremented}
        score={celebration.score}
        freezeUsed={celebration.freezeUsed}
        onDismiss={dismissCelebration}
      />
    </ActivePracticeContext.Provider>
  );
}
